import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Divider,
  Chip,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import {colors, spacing} from '../../theme';

const InvoiceDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {invoiceId} = route.params || {};
  const {fetchInvoiceDetail} = useSubscriptionStore();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchInvoiceDetail(invoiceId);
        setInvoice(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId, fetchInvoiceDetail]);

  const formatDate = useCallback(dateStr => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const getStatusColor = useCallback(status => {
    switch (status) {
      case 'PAID':
        return colors.success;
      case 'ISSUED':
        return colors.info;
      case 'OVERDUE':
        return colors.error;
      case 'VOID':
        return colors.textSecondary;
      default:
        return colors.warning;
    }
  }, []);

  const formatAmount = useCallback(val => {
    return val != null ? Number(val).toFixed(2) : '0.00';
  }, []);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!invoice) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction
            onPress={() => navigation.goBack()}
            color={colors.white}
          />
          <Appbar.Content
            title={t('myAccount.invoiceDetail')}
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        <Text style={styles.emptyText}>{t('common.noData')}</Text>
      </View>
    );
  }

  const statusColor = getStatusColor(invoice.invoiceStatus);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={t('myAccount.invoiceDetail')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.content}>
        {/* Invoice header */}
        <View style={styles.card}>
          <View style={styles.headerRow}>
            <Text style={styles.invoiceNo}>{invoice.invoiceNo}</Text>
            <Chip
              style={[styles.statusChip, {backgroundColor: statusColor + '18'}]}
              textStyle={[styles.statusChipText, {color: statusColor}]}
              compact>
              {invoice.invoiceStatus}
            </Chip>
          </View>

          <Divider style={styles.divider} />

          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>{t('myAccount.invoiceDate')}</Text>
              <Text style={styles.infoValue}>
                {formatDate(invoice.invoiceDate)}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>{t('myAccount.dueDate')}</Text>
              <Text style={styles.infoValue}>
                {formatDate(invoice.dueDate)}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>{t('myAccount.periodLabel')}</Text>
              <Text style={styles.infoValue}>
                {formatDate(invoice.periodStart)} -{' '}
                {formatDate(invoice.periodEnd)}
              </Text>
            </View>
          </View>
        </View>

        {/* Line items */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('myAccount.lineItems')}</Text>

          {/* Table header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.descCol]}>
              {t('myAccount.description')}
            </Text>
            <Text style={[styles.tableHeaderText, styles.qtyCol]}>
              {t('myAccount.qty')}
            </Text>
            <Text style={[styles.tableHeaderText, styles.priceCol]}>
              {t('myAccount.unitPrice')}
            </Text>
            <Text style={[styles.tableHeaderText, styles.amountCol]}>
              {t('myAccount.amount')}
            </Text>
          </View>

          <Divider />

          {(invoice.lines || []).map((line, idx) => (
            <View key={line.id || idx}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.descCol]} numberOfLines={2}>
                  {line.description}
                </Text>
                <Text style={[styles.tableCell, styles.qtyCol]}>
                  {line.qty}
                </Text>
                <Text style={[styles.tableCell, styles.priceCol]}>
                  {'\u20B9'}{formatAmount(line.unitPrice)}
                </Text>
                <Text style={[styles.tableCell, styles.amountCol]}>
                  {'\u20B9'}{formatAmount(line.amount)}
                </Text>
              </View>
              {line.periodStart ? (
                <Text style={styles.linePeriod}>
                  {formatDate(line.periodStart)} - {formatDate(line.periodEnd)}
                </Text>
              ) : null}
              <Divider />
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.card}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('myAccount.subtotal')}</Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{formatAmount(invoice.subTotal)}
            </Text>
          </View>
          {invoice.discountTotal > 0 && (
            <View style={styles.totalRow}>
              <Text style={[styles.totalLabel, styles.discountText]}>
                {t('myAccount.discount')}
              </Text>
              <Text style={[styles.totalValue, styles.discountText]}>
                -{'\u20B9'}{formatAmount(invoice.discountTotal)}
              </Text>
            </View>
          )}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('myAccount.tax')}</Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{formatAmount(invoice.taxTotal)}
            </Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.totalRow}>
            <Text style={styles.grandTotalLabel}>
              {t('myAccount.grandTotal')}
            </Text>
            <Text style={styles.grandTotalValue}>
              {'\u20B9'}{formatAmount(invoice.grandTotal)}
            </Text>
          </View>
        </View>

        {invoice.notes ? (
          <View style={styles.card}>
            <Text style={styles.notesText}>{invoice.notes}</Text>
          </View>
        ) : null}

        <View style={{height: spacing.xxl}} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  content: {flex: 1, padding: spacing.md},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  divider: {marginVertical: spacing.sm},

  // Header
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invoiceNo: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
  },
  statusChip: {height: 28},
  statusChipText: {fontSize: 12, fontWeight: '700'},

  // Info grid
  infoGrid: {gap: 10},
  infoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  infoLabel: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  infoValue: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
  },

  // Section title
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Table
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    alignItems: 'center',
  },
  tableCell: {
    fontSize: 13,
    color: colors.text,
  },
  descCol: {flex: 3},
  qtyCol: {flex: 1, textAlign: 'center'},
  priceCol: {flex: 2, textAlign: 'right'},
  amountCol: {flex: 2, textAlign: 'right'},
  linePeriod: {
    fontSize: 11,
    color: colors.textSecondary,
    paddingBottom: 6,
    paddingLeft: 4,
  },

  // Totals
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  totalValue: {
    fontSize: 14,
    color: colors.text,
  },
  discountText: {color: colors.success, fontWeight: '500'},
  grandTotalLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
  },
  grandTotalValue: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.primary,
  },

  notesText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
});

export default InvoiceDetailScreen;
